import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, User, Mail, Phone, Shield, Sun, Moon, PenLine, Send, CheckCircle2, XCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';
import { RichTextEditor } from '@/components/RichTextEditor';
import { sanitizeHtml } from '@/lib/sanitizeHtml';

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [signature, setSignature] = useState('');
  const [savingSig, setSavingSig] = useState(false);

  const isAdminOrOwner = user?.role === 'Administrator' || user?.role === 'Owner';
  const [emailCfg, setEmailCfg] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!isAdminOrOwner) return;
    api.get('/email/config').then(r => setEmailCfg(r.data)).catch(() => setEmailCfg(null));
  }, [isAdminOrOwner]);

  useEffect(() => { if (user?.email && !testTo) setTestTo(user.email); }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/email/test', { to: testTo });
      setTestResult(res.data);
      if (res.data.delivered) toast.success(`Test email sent to ${res.data.to}`);
      else toast.error(res.data.error || 'Test email failed');
    } catch (e) {
      const err = e.response?.data?.detail || 'Failed to send test email';
      setTestResult({ delivered: false, error: err });
      toast.error(err);
    }
    setTesting(false);
  };

  useEffect(() => { setSignature(user?.signature || ''); }, [user?.signature]);

  const handleSaveSignature = async () => {
    setSavingSig(true);
    try {
      await api.put('/auth/preferences', { signature });
      if (setUser && user) setUser({ ...user, signature });
      toast.success('Signature saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save signature');
    }
    setSavingSig(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) return toast.error('Enter your current password');
    if (!newPassword) return toast.error('Enter a new password');
    if (newPassword.length < 6) return toast.error('New password must be at least 6 characters');
    if (newPassword !== confirmPassword) return toast.error('Passwords do not match');

    setSaving(true);
    try {
      const res = await api.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(res.data.status || 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-2xl animate-fade-in" data-testid="profile-page">
      <div>
        <h1 className="font-heading font-semibold text-lg tracking-tight">My Profile</h1>
        <p className="text-sm text-muted-foreground">Manage your account settings</p>
      </div>

      {/* Account Info */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-semibold flex items-center gap-2">
            <User className="h-4 w-4 text-primary" /> Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center text-primary text-lg font-semibold shrink-0">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <p className="font-medium">{user?.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-xs">
                  <Shield className="h-2.5 w-2.5 mr-0.5" /> {user?.role}
                </Badge>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">{user?.email}</span>
            </div>
            {user?.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">{user?.phone}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-semibold flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                className="bg-background border-border"
                placeholder="Enter current password"
                data-testid="current-password"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="bg-background border-border"
                placeholder="At least 6 characters"
                data-testid="new-password"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="bg-background border-border"
                placeholder="Re-enter new password"
                data-testid="confirm-password"
              />
            </div>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground" data-testid="change-password-btn">
              <Lock className="h-4 w-4 mr-1.5" /> {saving ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Email Signature */}
      <Card className="border-border bg-card shadow-none" data-testid="signature-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-semibold flex items-center gap-2">
            <PenLine className="h-4 w-4 text-primary" /> Email Signature
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Set your name, title, and contact info once. It's automatically added to the bottom of every email you send from a project.
          </p>
          <RichTextEditor
            value={signature}
            onChange={setSignature}
            placeholder="Best regards,&#10;Your Name, Title&#10;NYCD Group · (212) 555-0100"
            minHeight={120}
            testid="signature-editor"
          />
          <div>
            <Label className="text-xs text-muted-foreground">Preview</Label>
            <div className="mt-1 rounded-md border border-border bg-background px-3 py-2 min-h-[52px]" data-testid="signature-preview">
              {signature && signature.trim() ? (
                <div className="email-html-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(signature) }} />
              ) : (
                <span className="text-xs text-muted-foreground">Your signature preview will appear here.</span>
              )}
            </div>
          </div>
          <Button onClick={handleSaveSignature} disabled={savingSig} className="bg-primary text-primary-foreground" data-testid="save-signature-btn">
            <PenLine className="h-4 w-4 mr-1.5" /> {savingSig ? 'Saving...' : 'Save Signature'}
          </Button>
        </CardContent>
      </Card>

      {/* Email Delivery (Administrators & Owners) */}
      {isAdminOrOwner && (
        <Card className="border-border bg-card shadow-none" data-testid="email-delivery-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading font-semibold flex items-center gap-2">
              <Send className="h-4 w-4 text-primary" /> Email Delivery
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Outgoing mail is sent over SMTP using the server's environment variables. Use this to confirm delivery is working on this environment.
            </p>

            <div className="rounded-md border border-border bg-background p-3 space-y-1.5" data-testid="email-config-status">
              {emailCfg ? (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Status</span>
                    {emailCfg.configured ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" data-testid="email-configured-yes">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Configured
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-600 font-medium" data-testid="email-configured-no">
                        <XCircle className="h-3.5 w-3.5" /> Not configured
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Server</span><span className="font-mono">{emailCfg.host}:{emailCfg.port} ({emailCfg.security})</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>From</span><span className="font-mono truncate max-w-[60%] text-right">{emailCfg.from_name} &lt;{emailCfg.from_email || 'not set'}&gt;</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Credentials</span>
                    <span>{emailCfg.user_configured ? 'user set' : 'no user'} · {emailCfg.password_configured ? 'password set' : 'no password'}</span>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Loading configuration...</p>
              )}
            </div>

            {emailCfg && !emailCfg.configured && (
              <p className="text-xs text-amber-600" data-testid="email-not-configured-note">
                SMTP credentials are empty in this environment, so mail will not be delivered. Set SMTP_USER and SMTP_APP_PASSWORD (plus SMTP_HOST/PORT/FROM) in the backend environment, then send a test below.
              </p>
            )}

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Send a test email to</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  value={testTo}
                  onChange={e => setTestTo(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-background border-border"
                  data-testid="email-test-to"
                />
                <Button
                  onClick={handleSendTest}
                  disabled={testing || !testTo.trim()}
                  className="bg-primary text-primary-foreground shrink-0"
                  data-testid="email-test-send-btn"
                >
                  <Send className="h-4 w-4 mr-1.5" /> {testing ? 'Sending...' : 'Send Test'}
                </Button>
              </div>
              {testResult && (
                <div
                  className={`text-xs rounded-md px-3 py-2 border ${testResult.delivered ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700' : 'bg-red-500/10 border-red-500/30 text-red-700'}`}
                  data-testid="email-test-result"
                >
                  {testResult.delivered
                    ? `Test email delivered to ${testResult.to || testTo}.`
                    : `Failed: ${testResult.error || 'Unknown error'}`}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading font-semibold flex items-center gap-2">
            {theme === 'dark' ? <Moon className="h-4 w-4 text-primary" /> : <Sun className="h-4 w-4 text-amber-400" />} Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</Label>
              <p className="text-xs text-muted-foreground">
                {theme === 'dark' ? 'Switch to light mode for a brighter look' : 'Switch to dark mode for a darker look'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Sun className={`h-3.5 w-3.5 ${theme === 'light' ? 'text-amber-400' : 'text-muted-foreground/40'}`} />
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={() => {
                  toggleTheme();
                  toast.success(theme === 'dark' ? 'Switched to Light Mode' : 'Switched to Dark Mode');
                }}
                data-testid="theme-toggle"
              />
              <Moon className={`h-3.5 w-3.5 ${theme === 'dark' ? 'text-primary' : 'text-muted-foreground/40'}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
